import type { PlayerPickupFeedback } from "./player-runtime";
import { gameplayConfig } from "../config/gameplay-config";
import type {
  PlayerMovementState,
  Vector2,
} from "../simulation/player-movement";

export interface PlayerStatusLayout {
  readonly badge: Vector2;
  readonly feedback: Vector2;
}

export interface RoundProgressPresentation {
  readonly assistiveMilestone: RoundProgressMilestone | null;
  readonly finalSection: boolean;
  readonly percent: number;
  readonly visible: boolean;
}

export interface RoundProgressMilestone {
  readonly percent: 0 | 25 | 50 | 75 | 100;
  readonly text: string;
}

export interface StatusViewport {
  readonly width: number;
  readonly x: number;
}

export const clampPlayerStatusX = (
  x: number,
  halfWidth: number,
  viewport: StatusViewport,
): number => {
  const padding = 8;
  const minimum = viewport.x + halfWidth + padding;
  const maximum = viewport.x + viewport.width - halfWidth - padding;
  return Math.max(minimum, Math.min(maximum, x));
};

export const formatPlayerBadge = (level: number): string =>
  `Lv ${String(level)}`;

const formatGrowthAmount = (growth: number): string =>
  String(Math.round(growth * 100) / 100);

export const formatPlayerFeedback = (feedback: PlayerPickupFeedback): string =>
  feedback.levelUp
    ? `Level ${String(feedback.level)}!`
    : `+${formatGrowthAmount(feedback.growth)}`;

export const getPlayerStatusLayout = (
  player: PlayerMovementState,
): PlayerStatusLayout => {
  const badgeY =
    player.position.y - player.size - gameplayConfig.playerStatus.badgeGap;
  return {
    badge: { x: player.position.x, y: badgeY },
    feedback: {
      x: player.position.x,
      y: badgeY - gameplayConfig.playerStatus.feedbackGap,
    },
  };
};

export const getRoundProgressPresentation = (
  timeRemainingMs: number | null,
  durationMs: number | null,
  finalWarningMs: number | null,
): RoundProgressPresentation => {
  if (
    timeRemainingMs === null ||
    durationMs === null ||
    finalWarningMs === null
  ) {
    return {
      assistiveMilestone: null,
      finalSection: false,
      percent: 0,
      visible: false,
    };
  }
  const ratio = Math.max(0, Math.min(1, timeRemainingMs / durationMs));
  const percent = ratio * 100;
  return {
    assistiveMilestone:
      percent <= 0
        ? { percent: 0, text: "Run complete." }
        : percent <= 25
          ? { percent: 25, text: "One quarter of the run left." }
          : percent <= 50
            ? { percent: 50, text: "Half of the run left." }
            : percent <= 75
              ? { percent: 75, text: "Three quarters of the run left." }
              : { percent: 100, text: "Run started." },
    finalSection: timeRemainingMs <= finalWarningMs,
    percent,
    visible: true,
  };
};
